import React, { useCallback, useMemo } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import noop from 'lodash/noop';

import { BasePopoverContent } from '../../BasePopoverContent';
import BottomSheet from '../../BottomSheet';
import Popper from '../../Popper';
import { eventStopPropagation, StopPropagationEventType } from '../../utils/events';
import { usePopoverContext } from '../PopoverContext';
import { getPopoverParams } from './styles';
import { PopoverContentProps } from './types';

const PopoverContent: React.FC<React.PropsWithChildren<PopoverContentProps>> = (props) => {
  const { anchorEl, onClose, open } = usePopoverContext();
  const {
    children,
    classes,
    className,
    container: containerProp,
    maxWidth,
    minWidth = 16,
    popperOptions,
    position,
    renderAs,
    useAnchorAsContainer,
    useAnchorElWidth = false,
  } = props;
  const container = useAnchorAsContainer ? anchorEl : containerProp;

  const onPopoverClose = useCallback(
    (event: StopPropagationEventType) => {
      eventStopPropagation(event);
      onClose();
    },
    [onClose],
  );

  const { anchorOrigin, popoverClasses, transformOrigin } = useMemo(
    () =>
      getPopoverParams({
        classes,
        className,
        maxWidth,
        minWidth: useAnchorElWidth && anchorEl?.clientWidth ? anchorEl.clientWidth / 10 : minWidth,
        position,
      }),
    [anchorEl, className, classes, maxWidth, minWidth, position, useAnchorElWidth],
  );

  if (renderAs === 'bottom-sheet') {
    return open ? (
      <BottomSheet open classes={classes?.BottomSheet} onClose={onClose} onOpen={noop}>
        {children}
      </BottomSheet>
    ) : null;
  }

  if (renderAs === 'popper') {
    if (!open) {
      return null;
    }

    return (
      <ClickAwayListener onClickAway={onClose}>
        <Popper
          open
          anchorEl={anchorEl}
          className={popoverClasses.paper}
          container={container}
          placement={position}
          popperOptions={popperOptions}>
          {children}
        </Popper>
      </ClickAwayListener>
    );
  }

  return (
    <BasePopoverContent
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      classes={popoverClasses}
      container={container}
      open={open}
      transformOrigin={transformOrigin}
      onClose={onPopoverClose}>
      {children}
    </BasePopoverContent>
  );
};

export { PopoverContentProps };
export default PopoverContent;
